import { createSlice } from "@reduxjs/toolkit";
import { getCabs, bookNow, createOrder, applyCoupon, getCustomerFAQ } from "./CabAction";
import { SESSION_EXPIRED_REJECT } from "../../utils/sessionCookie";

const cabSlice = createSlice({
    name: 'cab',
    initialState: {
        loading: true,
        error: false,
        sessionExpiredRedirect: false,
        cabs: [],
        visitorEntryId: '',
        web_trip_unique_id: '',
        distance: '',
        bookingSuccess: false,
        orderSuccess: false,
        order: {},
        couponApplied: {},
        couponAppliedSuccess: false,
        couponAppliedError: false,
        customerFAQ: []
    },
    reducers: {
        clearError: (state) => {
            state.error = false;
        },
        clearBookingSuccess: (state) => {
            state.bookingSuccess = false;
        },
        clearOrderSuccess: (state) => {
            state.orderSuccess = false;
        },
        clearCouponAppliedError: (state) => {
            state.couponAppliedError = false;
        },
        clearCouponAppliedSuccess: (state) => {
            state.couponAppliedSuccess = false;
        },
        setCabSearchResult: (state, action) => {
            const payload = action.payload || {};
            state.cabs = payload.data ?? [];
            state.visitorEntryId = payload.visitorEntryId ?? '';
            state.web_trip_unique_id = payload.web_trip_unique_id ?? '';
            state.distance = payload.distance ?? '';
            state.loading = false;
            state.error = false;
        },
        clearSessionExpired: (state) => {
            state.sessionExpiredRedirect = false;
        },
        /** Clear cab search / visitor context (pair with client storage clears) */
        resetCabBookingContext: (state) => {
            state.cabs = [];
            state.visitorEntryId = "";
            state.web_trip_unique_id = "";
            state.distance = "";
            state.loading = true;
            state.error = false;
            state.couponApplied = {};
            state.couponAppliedSuccess = false;
            state.couponAppliedError = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getCabs.pending, (state) => {
                state.loading = true;
                state.cabs = [];
                state.error = false;
            })
            .addCase(getCabs.fulfilled, (state, action) => {
                state.cabs = action?.payload?.data?.data;
                state.visitorEntryId = action?.payload?.data?.visitorEntryId;
                state.web_trip_unique_id = action?.payload?.data?.web_trip_unique_id;
                state.distance = action?.payload?.data?.distance;
                state.loading = false;
                state.error = false;
            })
            .addCase(getCabs.rejected, (state, action) => {
                state.loading = false;

                // Ignore duplicate/cancelled get_trip_fare calls so the successful
                // response does not get cleared from the UI.
                if (!action.payload) {
                    state.error = false;
                    return;
                }

                state.cabs = [];
                if (action.payload === SESSION_EXPIRED_REJECT) {
                    state.sessionExpiredRedirect = true;
                    state.error = false;
                } else {
                    state.error = action.payload;
                }
            })
            .addCase(bookNow.pending, (state) => {
                state.loading = true;
            })
            .addCase(bookNow.fulfilled, (state, action) => {
                state.loading = false;
                state.bookingSuccess = true;
                state.error = false;
            })
            .addCase(bookNow.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.bookingSuccess = false;
            })
            .addCase(createOrder.pending, (state) => {
                state.loading = true;
            })
            .addCase(createOrder.fulfilled, (state, action) => {
                state.loading = false;
                state.orderSuccess = true;
                state.order = action?.payload?.data;
                state.error = false;
            })
            .addCase(createOrder.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.orderSuccess = false;
            })
            .addCase(applyCoupon.pending, (state) => {
                state.loading = true;
            })
            .addCase(applyCoupon.fulfilled, (state, action) => {
                state.loading = false;
                state.couponApplied = action?.payload?.data;
                state.error = false;
                state.couponAppliedSuccess = true;
            })
            .addCase(applyCoupon.rejected, (state, action) => {
                state.loading = false;
                state.couponApplied = {};
                state.couponAppliedError = true;
                state.couponAppliedSuccess = false;
            })
            .addCase(getCustomerFAQ.pending, (state) => {
                state.loading = true;
            })
            .addCase(getCustomerFAQ.fulfilled, (state, action) => {
                state.loading = false;
                state.customerFAQ = action?.payload?.data?.data;
                state.error = false;
            })
            .addCase(getCustomerFAQ.rejected, (state, action) => {
                state.loading = false;
                state.error = true;
            });
    }
});

const cabAction = cabSlice.actions;

export { cabAction, cabSlice };
